<template>
  <div class="compensation-view" v-if="kvdr && kvdr.compensation">
    
    <!-- CASH COMPENSATION SECTION -->
    <div class="metric-card">
      <div class="section-title">
        <span class="icon text-orange">
          <!-- Coins -->
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="8" cy="8" r="5"></circle>
            <path d="M13 8h3a5 5 0 0 1 0 10H11"></path>
            <path d="M8 5v6"></path>
            <path d="M5 8h6"></path>
          </svg>
        </span>
        CASH COMPENSATION
      </div>
      
      <div class="grid-2 mt-2">
        <div>
          <label>Base salary (annual)</label>
          <div class="input-wrap">
            <span class="sym">{{ currentCurrencySymbol }}</span>
            <input type="number" v-model.number="kvdr.compensation.baseSalary">
          </div>
        </div>

        <div>
          <label>Employment cost multiplier</label>
          <div class="input-wrap">
            <input type="number" step="0.1" v-model.number="kvdr.compensation.employmentCostMultiplier">
          </div>
          <p class="hint">Default 1.3× covers tax, benefits.</p>
        </div>

        <div>
          <label>Variable comp / bonus (% of base)</label>
          <div class="input-wrap">
            <input type="number" v-model.number="kvdr.compensation.variablePct">
          </div>
        </div>

        <div>
          <label>Equity (annualized)</label>
          <div class="input-wrap">
            <span class="sym">{{ currentCurrencySymbol }}</span>
            <input type="number" v-model.number="kvdr.compensation.equityAnnualized">
          </div>
        </div>
      </div>
    </div>


    <!-- MORALE & NON-CASH PERKS SECTION -->
    <div class="metric-card">
      <div class="flex-between">
        <div class="section-title">
          <span class="icon text-orange">
            <!-- Heart -->
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20.8 8.7c0 5.2-8.8 10.3-8.8 10.3S3.2 13.9 3.2 8.7A4.7 4.7 0 0 1 12 6.4a4.7 4.7 0 0 1 8.8 2.3z"></path>
            </svg>
          </span>
          MORALE & NON-CASH PERKS
        </div>

        <span class="hint">Auto-rolled into total cost</span>
      </div>
      
      <div class="grid-2 mt-8">

        <div>
          <label>
            <span class="icon">
              <!-- Calendar -->
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="4" width="18" height="17" rx="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
            </span>
            Extra time-off (days/yr)
          </label>

          <div class="input-wrap">
            <input type="number" v-model.number="kvdr.compensation.extraTimeOffDays">
          </div>

          <p class="hint">
            ≈ {{ currentCurrencySymbol }}{{ formatNumber((kvdr.compensation.baseSalary || 0) / 220) }} / day
          </p>
        </div>


        <div>
          <label>
            <span class="icon">
              <!-- Graduation cap -->
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M2 10l10-5 10 5-10 5L2 10z"></path>
                <path d="M6 12.5V17c3 2 9 2 12 0v-4.5"></path>
                <path d="M22 10v6"></path>
              </svg>
            </span>
            Learning budget
          </label>

          <div class="input-wrap">
            <span class="sym">{{ currentCurrencySymbol }}</span>
            <input type="number" v-model.number="kvdr.compensation.learningBudget">
          </div>
        </div>


        <div>
          <label>
            <span class="icon">
              <!-- Users -->
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </span>
            Team support — interns
          </label>

          <div class="input-wrap">
            <input type="number" v-model.number="kvdr.compensation.interns">
          </div>

          <p class="hint">≈ {{ currentCurrencySymbol }}25,000 / intern</p>
        </div>


        <div>
          <label>Flexibility score (1-5)</label>

          <input
            type="range"
            min="1"
            max="5"
            v-model.number="kvdr.compensation.flexibilityScore"
            class="slider"
            :style="{
              '--fill-percent':
                ((kvdr.compensation.flexibilityScore - 1) / 4) * 100 + '%'
            }"
          >
        </div>

      </div>


      <div class="pill-group mt-8">

        <button
          class="pill"
          :class="{active: kvdr.compensation.assistant}"
          @click="kvdr.compensation.assistant = !kvdr.compensation.assistant"
        >
          <span class="icon">
            <!-- User -->
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="7" r="4"></circle>
              <path d="M5.5 21a6.5 6.5 0 0 1 13 0"></path>
            </svg>
          </span>
          Dedicated assistant
        </button>


        <button
          class="pill"
          :class="{active: kvdr.compensation.recognitionRewards}"
          @click="kvdr.compensation.recognitionRewards = !kvdr.compensation.recognitionRewards"
        >
          <span class="icon">
            <!-- Award -->
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="8" r="5"></circle>
              <path d="M8.5 12L7 22l5-3 5 3-1.5-10"></path>
              <path d="M10 8l1.5 1.5L14 7"></path>
            </svg>
          </span>
          Recognition rewards
        </button>

      </div>


      <div class="perks-text-area-wrapper mt-8">
        <textarea
          v-model="kvdr.compensation.otherPerks"
          class="text-input"
          placeholder="Other perks (sabbatical, gym, equipment, retreats...)"
        ></textarea>

        <div class="perks-proxy-summary">
          Perks proxy cost:
          <strong>
            {{ currentCurrencySymbol }}{{ formatNumber(perksProxyCost) }}
          </strong>
        </div>
      </div>

    </div>


    <!-- VALUE / COST ALIGNMENT -->
    <div :class="['alignment-box', alignmentBandClass]">

      <div class="ab-header">

        <span class="ab-title">
          <span class="icon">
            <!-- Link -->
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10 13a5 5 0 0 0 7.1.1l2-2a5 5 0 0 0-7.1-7.1l-1.1 1.1"></path>
              <path d="M14 11a5 5 0 0 0-7.1-.1l-2 2a5 5 0 0 0 7.1 7.1l1.1-1.1"></path>
            </svg>
          </span>
          VALUE / COST ALIGNMENT
        </span>


        <span class="ab-badge">

          <span class="icon" v-if="alignmentBand === 'Aligned'">
            <!-- Check circle -->
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="9"></circle>
              <polyline points="8 12 11 15 16 9"></polyline>
            </svg>
          </span>

          <span class="icon" v-if="alignmentBand === 'Under-rewarded'">
            <!-- Warning -->
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10.3 3.9L2.2 18a2 2 0 0 0 1.7 3h16.2a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12" y2="17"></line>
            </svg>
          </span>

          <span
            class="icon"
            v-if="alignmentBand === 'Over-costed' || alignmentBand === 'Undefined'"
          >
            <!-- Alert -->
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="9"></circle>
              <line x1="12" y1="8" x2="12" y2="13"></line>
              <line x1="12" y1="16" x2="12" y2="16"></line>
            </svg>
          </span>

          {{ alignmentBand.toUpperCase() }}
        </span>

      </div>


      <div class="ab-metrics">

        <div class="ab-metric">
          <span class="ab-m-label">TOTAL COST</span>
          <span class="ab-m-val">
            {{ currentCurrencySymbol }}{{ formatNumber(totalEmploymentCost) }}
          </span>
        </div>

        <div class="ab-metric">
          <span class="ab-m-label">ADJ. VALUE</span>
          <span class="ab-m-val">
            {{ currentCurrencySymbol }}{{ formatNumber(adjustedValue) }}
          </span>
        </div>

        <div class="ab-metric">
          <span class="ab-m-label">V / C RATIO</span>
          <span class="ab-m-val">
            {{ vcRatio.toFixed(2) }}×
          </span>
        </div>

      </div>


      <div class="ab-logic">

        <span class="ab-title">
          <span class="icon">
            <!-- Sparkles -->
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3z"></path>
              <path d="M19 16l.7 2.3L22 19l-2.3.7L19 22l-.7-2.3L16 19l2.3-.7L19 16z"></path>
            </svg>
          </span>
          RECOMMENDED COMP LOGIC
        </span>

        <strong class="ab-logic-val">{{ compLogicRecommendation }}</strong>

        <span class="ab-logic-sub">
          Based on KVDR score {{ totalScore }}/25 ·
          {{ directContributionPct }}% direct ·
          V/C {{ vcRatio.toFixed(2) }}×
        </span>

      </div>

    </div>

  </div>
</template>

<script>
export default {
  name: 'CompensationLayer',
  props: {
    kvdr: { type: Object, required: true }, 
    currencies: { type: Array, required: true }, 
    currentCurrencySymbol: { type: String, required: true },
    formatNumber: { type: Function, required: true },
    adjustedValue: { type: Number, required: true },
    totalEmploymentCost: { type: Number, required: true },
    vcRatio: { type: Number, required: true },
    alignmentBand: { type: String, required: true },
    compLogicRecommendation: { type: String, required: true },
    totalScore: { type: Number, required: true },
    directContributionPct: { type: Number, required: true },
    perksProxyCost: { type: Number, required: true }
  },
  computed: {
    alignmentBandClass() {
      // Adjusted colors based on specific prompt
      if (this.alignmentBand === 'Aligned') return 'band-aligned'; // Green
      if (this.alignmentBand === 'Under-rewarded') return 'band-under'; // Orange warning
      if (this.alignmentBand === 'Over-costed') return 'band-over'; // Red
      return 'band-over'; // Fallback / Undefined is Red
    }
  }
}
</script>

<style scoped>
/* Standardized Spacing */
.compensation-view { display: flex; flex-direction: column; gap: 20px; padding-bottom: 24px; }
.grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; row-gap: 12px; } 
.mt-8 { margin-top: 8px; }

/* Uniform card padding */
.metric-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 24px; display: flex; flex-direction: column; gap: 8px; }
.section-title { font-size: 11px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.5px; display: flex; align-items: center; gap: 6px; border-bottom: 1px dashed #e2e8f0; padding-bottom: 8px; }
.text-orange { color: #F59E0B; }

label { font-size: 11px; font-weight: 600; color: #64748b; display: flex; align-items: center; gap: 6px; margin-bottom: 4px; }
.icon { font-size: 13px; }
.hint { font-size: 11px; color: #94a3b8; margin: 2px 0 0 0; line-height: 1.2; }

.input-wrap { position: relative; display: flex; align-items: center; }
.sym { position: absolute; left: 12px; color: #64748b; font-weight: 600; font-size: 13px; pointer-events: none; }
.input-wrap input { width: 100%; padding: 8px 12px; border: 1px solid #cbd5e1; border-radius: 6px; font-size: 13px; outline: none; transition: 0.2s; } 
.input-wrap input:focus { border-color: #F59E0B; }
.sym + input { padding-left: 28px; }

.flex-between { display: flex; justify-content: space-between; align-items: flex-end; }

.pill-group { display: flex; flex-wrap: wrap; gap: 8px; }
.pill { padding: 6px 14px; border-radius: 20px; border: 1px solid #cbd5e1; background: #fff; font-size: 12px; color: #475569; cursor: pointer; font-weight: 500; transition: all 0.2s; display: flex; align-items: center; gap: 6px; }
.pill:hover { border-color: #94a3b8; }
.pill.active { background: #Fef3c7; border-color: #F59E0B; color: #d97706; font-weight: 600; }

/* FIXED: Slider CSS variables support */
.slider { width: 100%; appearance: none; height: 6px; border-radius: 4px; outline: none; background: linear-gradient(to right, #F59E0B 0%, #F59E0B var(--fill-percent, 0%), #e2e8f0 var(--fill-percent, 0%), #e2e8f0 100%); margin: 6px 0; }
.slider::-webkit-slider-thumb { appearance: none; width: 16px; height: 16px; border-radius: 50%; background: #F59E0B; cursor: pointer; border: 2px solid #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.3); }

.perks-text-area-wrapper { border: 1px solid #cbd5e1; border-radius: 6px; overflow: hidden; display: flex; flex-direction: column; transition: border-color 0.2s; }
.perks-text-area-wrapper:focus-within { border-color: #F59E0B; }
.text-input { width: 100%; padding: 10px; border: none; font-size: 13px; height: 50px; resize: vertical; outline: none; font-family: inherit; } 
.perks-proxy-summary { background: #f8fafc; padding: 6px 12px; font-size: 11px; color: #64748b; text-align: right; border-top: 1px dashed #e2e8f0; }
.perks-proxy-summary strong { color: #0f172a; font-weight: 700; }

/* 🌟 DYNAMIC ALIGNMENT BOX CSS 🌟 */
.alignment-box { border-radius: 8px; padding: 24px; display: flex; flex-direction: column; gap: 16px; border: 1px solid transparent; transition: all 0.3s ease; }

/* 1. Aligned (Green perfectly aligned) */
.band-aligned { background: #ECFDF5; border-color: #6EE7B7; }
.band-aligned .ab-title { color: #059669; }
.band-aligned .ab-badge { color: #059669; font-weight: 800; }
.band-aligned .ab-m-val { color: #059669; }
.band-aligned .ab-logic-val { color: #059669; }

/* 2. Under-rewarded (Orange warning) */
.band-under { background: #FFFBEB; border-color: #FCD34D; }
.band-under .ab-title { color: #D97706; }
.band-under .ab-badge { color: #D97706; font-weight: 800; }
.band-under .ab-m-val { color: #D97706; }
.band-under .ab-logic-val { color: #D97706; }

/* 3. Over-costed / Undefined (Red excessive payout) */
.band-over { background: #FEF2F2; border-color: #FCA5A5; }
.band-over .ab-title { color: #DC2626; }
.band-over .ab-badge { color: #DC2626; font-weight: 800; }
.band-over .ab-m-val { color: #DC2626; }
.band-over .ab-logic-val { color: #DC2626; }

.ab-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 1px dashed rgba(0,0,0,0.1); padding-bottom: 10px; }
.ab-title { font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; display: flex; align-items: center; gap: 6px; }
.ab-badge { font-size: 12px; display: flex; align-items: center; gap: 4px; }

.ab-metrics { display: flex; gap: 40px; }
.ab-metric { display: flex; flex-direction: column; gap: 2px; }
.ab-m-label { font-size: 10px; font-weight: 700; color: #94a3b8; text-transform: uppercase; }
.ab-m-val { font-size: 18px; font-weight: 800; }

.ab-logic { display: flex; flex-direction: column; gap: 4px; margin-top: 4px; }
.ab-logic-val { font-size: 16px; font-weight: 800; }
.ab-logic-sub { font-size: 11px; color: #94a3b8; font-weight: 500; }
.metric-card .icon,
.section-title .icon,
.ab-title .icon,
.ab-badge .icon,
.pill .icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    vertical-align: middle;
}

.metric-card .icon svg,
.section-title .icon svg,
.ab-title .icon svg,
.ab-badge .icon svg,
.pill .icon svg {
    width: 16px;
    height: 16px;
    display: block;
    stroke: currentColor;
}
</style>